import {
  ALL_CLICK_ID_KEYS,
  GOOGLE_ADS_KEYS,
  MARKETING_PARAM_KEYS,
  MarketingParamKey,
  MarketingParams,
  PASSTHROUGH_KEYS,
  PLATFORM_CLICK_ID_KEYS,
  UTM_KEYS,
} from './constants'
import { normalizeMarketingParamKey, normalizeTwReferrerKey } from './normalize'

/**
 * Pull only the allowlisted marketing params out of a query string. Non-marketing
 * params are ignored. Empty values are skipped.
 */
export function extractMarketingParams(search: string): MarketingParams {
  const params = new URLSearchParams(
    search && search.startsWith('?') ? search.slice(1) : search
  )

  const result: MarketingParams = {}

  params.forEach((value, rawKey) => {
    const canonical = normalizeMarketingParamKey(rawKey)

    if (canonical && value && !(canonical in result)) {
      result[canonical] = value
    }
  })

  return result
}

/**
 * Read `tw_referrer` from a query string when present. Accepts search with or
 * without a leading `?`.
 */
export function extractTwReferrerFromSearch(search: string): string | null {
  if (!search) {
    return null
  }

  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )

  let referrer: string | null = null

  params.forEach((value, rawKey) => {
    if (referrer === null && normalizeTwReferrerKey(rawKey) && value) {
      referrer = value
    }
  })

  return referrer
}

function getPlatformsPresent(params: MarketingParams): Set<string> {
  const platforms = new Set<string>()

  Object.entries(PLATFORM_CLICK_ID_KEYS).forEach(([platform, keys]) => {
    if (keys.some((key) => !!params[key])) {
      platforms.add(platform)
    }
  })

  return platforms
}

function hasAnyUtm(params: MarketingParams): boolean {
  return UTM_KEYS.some((key) => !!params[key])
}

function hasAnyGoogleAds(params: MarketingParams): boolean {
  return GOOGLE_ADS_KEYS.some((key) => !!params[key])
}

function hasAnyMarketingValue(params: MarketingParams): boolean {
  return Object.keys(params).length > 0
}

function hasAnyPassthrough(params: MarketingParams): boolean {
  return PASSTHROUGH_KEYS.some((key) => !!params[key])
}

function hasOnlyPassthrough(params: MarketingParams): boolean {
  return (
    hasAnyMarketingValue(params) &&
    Object.keys(params).every((key) =>
      PASSTHROUGH_KEYS.includes(key as MarketingParamKey)
    )
  )
}

/**
 * Returns true when `incoming` represents a fresh ad arrival relative to
 * `existing`: a different platform's click-id, a newer click-id value for the
 * same platform, a different utm set, or different Google Ads value-track params.
 * Passthrough keys (sfId, sfObjectType, partnershipCode) are not ad signals and
 * are ignored.
 */
export function isNewAdArrival(
  existing: MarketingParams,
  incoming: MarketingParams
): boolean {
  if (!hasAnyMarketingValue(incoming)) {
    return false
  }

  // A passthrough-only arrival is never a new ad arrival.
  if (hasOnlyPassthrough(incoming)) {
    return false
  }

  const incomingPlatforms = getPlatformsPresent(incoming)
  const existingPlatforms = getPlatformsPresent(existing)

  // A platform present in incoming but not existing => new arrival.
  const hasNewPlatform = Array.from(incomingPlatforms).some(
    (platform) => !existingPlatforms.has(platform)
  )

  if (hasNewPlatform) {
    return true
  }

  // Same platform but different click-id value => newer link.
  const hasNewClickId = ALL_CLICK_ID_KEYS.some(
    (key) => incoming[key] && incoming[key] !== existing[key]
  )

  if (hasNewClickId) {
    return true
  }

  // Different Google Ads value-track params => new campaign arrival.
  if (
    GOOGLE_ADS_KEYS.some(
      (key) => incoming[key] && incoming[key] !== existing[key]
    )
  ) {
    return true
  }

  // Different utm values => new campaign arrival.
  return UTM_KEYS.some(
    (key) => incoming[key] && incoming[key] !== existing[key]
  )
}

/**
 * Merge incoming marketing params over an existing set, applying override rules:
 * - When `incoming` contains click-ids for a platform, that platform's click-ids
 *   win and every OTHER platform's stale click-ids are cleared (cross-platform
 *   override, e.g. Meta arrival clears Google). Google Ads value-track params are
 *   also cleared on any click-id arrival.
 * - Same-platform newer link with different click-id values overrides.
 * - When `incoming` contains any Google Ads value-track key, the whole group is
 *   replaced by the incoming values (only keys actually present are kept).
 * - When `incoming` contains any utm key, the whole utm group is replaced by the
 *   incoming utm values (only the utm keys actually present are kept).
 * - Passthrough keys (sfId, sfObjectType, partnershipCode) are always copied
 *   as-is when present; they are never cleared by click-id or UTM logic.
 * - If `incoming` has no marketing values, `existing` is returned unchanged.
 */
export function mergeMarketingParams(
  existing: MarketingParams,
  incoming: MarketingParams
): MarketingParams {
  if (!hasAnyMarketingValue(incoming)) {
    return { ...existing }
  }

  const incomingPlatforms = getPlatformsPresent(incoming)
  const merged: MarketingParams = { ...existing }

  if (incomingPlatforms.size > 0) {
    // A new platform set arrived: clear ALL existing click-ids, then apply the
    // incoming platform click-ids. This handles both cross-platform override and
    // same-platform refresh deterministically.
    ALL_CLICK_ID_KEYS.forEach((key) => {
      delete merged[key]
    })

    ALL_CLICK_ID_KEYS.forEach((key) => {
      if (incoming[key]) {
        merged[key] = incoming[key]
      }
    })

    // Click-id arrivals from non-Google platforms should not keep stale Google Ads
    // value-track params; Google click-id arrivals will re-apply them below if present.
    GOOGLE_ADS_KEYS.forEach((key) => {
      delete merged[key]
    })
  }

  if (hasAnyGoogleAds(incoming)) {
    if (incomingPlatforms.size === 0) {
      ALL_CLICK_ID_KEYS.forEach((key) => {
        if (!PLATFORM_CLICK_ID_KEYS.google.includes(key)) {
          delete merged[key]
        }
      })
    }

    // Replace the Google Ads value-track group with the incoming values.
    GOOGLE_ADS_KEYS.forEach((key) => {
      delete merged[key]
    })

    GOOGLE_ADS_KEYS.forEach((key) => {
      if (incoming[key]) {
        merged[key] = incoming[key]
      }
    })
  }

  if (hasAnyUtm(incoming)) {
    // Replace the utm group with the incoming utm values.
    UTM_KEYS.forEach((key) => {
      delete merged[key]
    })

    UTM_KEYS.forEach((key) => {
      if (incoming[key]) {
        merged[key] = incoming[key]
      }
    })
  }

  // Passthrough keys are always copied when present; the click-id/UTM clearing
  // above must never remove them.
  if (hasAnyPassthrough(incoming)) {
    PASSTHROUGH_KEYS.forEach((key) => {
      if (incoming[key]) {
        merged[key] = incoming[key]
      }
    })
  }

  return merged
}

/**
 * Serialize marketing params into a query string fragment (no leading `?`),
 * preserving the canonical key order from MARKETING_PARAM_KEYS.
 */
export function serializeMarketingParams(params: MarketingParams): string {
  const search = new URLSearchParams()

  MARKETING_PARAM_KEYS.forEach((key) => {
    if (params[key]) {
      search.set(key, params[key] as string)
    }
  })

  return search.toString()
}

export function marketingParamsEqual(
  a: MarketingParams,
  b: MarketingParams
): boolean {
  const keys = Array.from(
    new Set<MarketingParamKey>([
      ...(Object.keys(a) as MarketingParamKey[]),
      ...(Object.keys(b) as MarketingParamKey[]),
    ])
  )

  return keys.every((key) => a[key] === b[key])
}
